// Lightweight hotkey normalization + mapping to intents
// Centralizes keyboard handling and routes to gesture machine or app dispatch

import { Dispatcher } from "@/events/atoms";
import { atom } from "jotai";


type Mods = { meta: boolean; ctrl: boolean; shift: boolean; alt: boolean }

export type HotkeyCtx = {
  // Controller for handling interactions
  controller: {
    handleEscape: () => void
    handleEnter: () => void
    toggleRectangleFill: () => void
    cycleShape: () => void
    enterShapeModeIfNeeded: () => void
    getCurrentMode: () => string
  } | null
  dispatch: Dispatcher
  // Accessor for current UI state
  getUi: () => any
}

export type Binding = {
  combo: string | string[]
  when?: string | ((ctx: HotkeyCtx) => boolean)
  preventDefault?: boolean
  repeat?: 'allow' | 'block'
  run: (ctx: HotkeyCtx) => void
}

// Utility: check if an input/textarea/contenteditable has focus
export function isEditableActive(): boolean {
  const el = (typeof document !== 'undefined') ? (document.activeElement as HTMLElement | null) : null
  if (!el) return false
  const tag = el.tagName?.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || el.isContentEditable) return true
  return false
}

// Normalize key/mods from gateway into a canonical combo string (e.g., "mod+shift+z", "s", "Escape")
export function comboFromGateway(key: string, mods: Mods): string | null {
  // Use the key as-is from w3c-keyname (e.g., 'Escape', 'Enter', 'Space', 'a', 'S')
  // Convert single letters to lowercase for consistency
  const keyPart = key.length === 1 ? key.toLowerCase() : key

  let parts: string[] = []
  // Special case: meta key pressed alone → 'Meta'
  if (keyPart === 'Meta') {
    return 'Meta'
  }
  if (mods.meta || mods.ctrl) parts.push('mod')
  if (mods.shift) parts.push('shift')
  if (mods.alt) parts.push('alt')
  parts.push(keyPart)
  return parts.join('+')
}

// Create a handler to be used with gestureGateway KEY_DOWN events
// `bindings` is read on every key press, so the active set can change while the handler is bound.
export function createGatewayHotkeyHandler(bindings: () => readonly Binding[], getCtx: () => HotkeyCtx) {
  return (e: { key: string; mods: Mods; repeat?: boolean; originalEvent?: KeyboardEvent }) => {
    if (isEditableActive()) return
    const combo = comboFromGateway(e.key, e.mods)
    if (!combo) return
    
    const ctx = getCtx()
    
    for (const b of bindings()) {
      const combos = Array.isArray(b.combo) ? b.combo : [b.combo]
      if (!combos.includes(combo)) continue
      if (b.repeat !== 'allow' && e.repeat) continue

      if (typeof b.when === 'string') {
        // String-based when clauses no longer supported without state machine
        continue
      } else if (typeof b.when === 'function') {
        if (!b.when(ctx)) continue
      }

      // Actually prevent default on the original event
      if (b.preventDefault !== false && e.originalEvent) {
        e.originalEvent.preventDefault()
      }
      b.run(ctx)
      break
    }
  }
}

/**
 * The key bindings in force, replacing the editor's own (`defaultBindings`) while set. A screen with
 * its own tools (a page) sets this to `coreBindings()` plus its own keys, so the editor's tool keys
 * cannot switch to a tool that screen does not have. Null: the editor's bindings.
 */
export const hotkeyBindingsAtom = atom<readonly Binding[] | null>(null)

// The editor's bindings: the core ones plus its tool keys.
export function defaultBindings(): Binding[] {
  return [...coreBindings(), ...editorToolBindings()]
}

// Bindings every screen keeps: history, cancel/commit, and the rectangle fill toggle.
export function coreBindings(): Binding[] {
  return [
    // Undo / Redo
    { combo: 'mod+z', run: ({ dispatch }) => dispatch(['undo']) },
    { combo: ['mod+shift+z', 'mod+y'], run: ({ dispatch }) => dispatch(['redo']) },

    // Escape - cancel current operation
    { 
      combo: 'Escape', 
      run: ({ controller }) => controller?.handleEscape() 
    },
    
    // Enter - commit selection
    { 
      combo: 'Enter',
      when: ({ controller }) => controller?.getCurrentMode() === 'selectionEditing',
      run: ({ controller }) => controller?.handleEnter() 
    },

    // Delete selection contents
    {
      combo: ['Backspace', 'Delete'],
      when: ({ getUi }) => {
        const ui = getUi()
        return !!ui?.voxelSelection || !!ui?.faceSelection
      },
      run: ({ dispatch }) => {
        dispatch(['doc.selection.delete'])
      }
    },

    // Rectangle fill toggle while drawing (Meta key or Space)
    {
      combo: 'Meta',
      preventDefault: false,
      when: ({ controller }) => controller?.getCurrentMode() === 'rectangle',
      run: ({ controller }) => controller?.toggleRectangleFill()
    },
    {
      combo: ' ',  // Space key
      when: ({ controller }) => controller?.getCurrentMode() === 'rectangle',
      run: ({ controller }) => controller?.toggleRectangleFill()
    },
  ]
}

// The editor's tool keys (and select all, which only the editor's select tool uses).
export function editorToolBindings(): Binding[] {
  return [
    {
      combo: 'mod+a',
      run: ({ dispatch }) => dispatch(['selection.change.selectAll'])
    },

    // Tool shortcuts
    {
      combo: 'e',
      run: ({ dispatch, getUi }) => {
        const tool = getUi().tool
        dispatch(['ui.setTool', tool.currentTool === 'eraser' ? 'scribble' : 'eraser'])
      }
    },
    { combo: 'v', run: ({ dispatch }) => dispatch(['ui.setTool', 'select']) },
    { combo: 'b', run: ({ dispatch }) => dispatch(['ui.setTool', 'paintbrush']) },
    { combo: 'd', run: ({ dispatch }) => dispatch(['ui.setTool', 'scribble']) },
    { combo: 'i', run: ({ dispatch }) => dispatch(['ui.setTool', 'eyedropper']) },

    // Shape: select or cycle
    {
      combo: 's',
      run: ({ dispatch, getUi, controller }) => {
        const tool = getUi().tool
        if (tool.currentTool === 'shape') {
          const mode = controller?.getCurrentMode()
          const isInShape = mode === 'shapeMode' || mode === 'shapePlacing' || mode === 'shapeHeight'
          if (isInShape) {
            // Cycle through shapes while in shape mode
            controller?.cycleShape()
          } else {
            // Enter shape mode
            controller?.enterShapeModeIfNeeded()
          }
        } else {
          // Switch to shape tool
          const events: any[] = []
          if (!tool.selectedShape) events.push(['ui.tool.selectedShape', 'sphere'] as const)
          events.push(['ui.setTool', 'shape'] as const)
          dispatch(events as any)
        }
      }
    }
  ]
}
